import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, valuesOf } from '../skillUsage';

export const driftAndHoldSkill: CodeoSkill = {
    name: SKILL_NAMES.driftAndHold,
    kind: 'codeo',
    description:
        'Give the whole frame a slow continuous drift that never stops, so a settled composition ' +
        'stays alive without anything in it appearing to move — the cure for a dead tail.',
    documentUsesSkill: check(
        (script) =>
            /ease:\s*['"]none/.test(script) &&
            (valuesOf(script, 'duration').some((value) => value >= 3) ||
                /duration:\s*[A-Za-z_]/.test(script)),
    ),
    instructions: `# Drift And Hold

The entire composition moves very slowly and continuously — a few pixels of translation, a percent of scale — for the whole scene. Nothing reads as animating; the frame simply never stops. It is the single most reliable fix for the most common defect in a generated graphic: content lands, and then the last three seconds are a still image.

Unlike a camera push, this has no destination and no arc. It is a bed, not a move.

## Markup

Everything the drift should carry goes inside one stage. Anything that must stay pinned to the frame edge — a lower third, a full-bleed wash — stays outside it.

\`\`\`html
<div id="drift-stage" style="position: absolute; inset: 0;">
  <div style="position: absolute; left: 220px; top: 400px; width: 1200px; font-family: var(--l5-font-0), sans-serif; font-size: 118px; font-weight: 800; line-height: 1.04; color: var(--l5-color-0, #101820);">Quiet by default</div>
  <div style="position: absolute; left: 220px; top: 660px; font-family: var(--l5-font-0), sans-serif; font-size: 40px; font-weight: 500; color: var(--l5-color-1, #5b6472);">Nothing you didn't ask for</div>
</div>
\`\`\`

## Timeline code

Scale slightly over the whole scene and drift a little across it. Both must run for the full timeline, not just the entrance.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const SCENE = 7;

gsap.set('#drift-stage', { scale: 1.015, x: -10, y: 6, transformOrigin: '50% 50%' });

tl.to('#drift-stage', {
    scale: 1.045,
    x: 12,
    y: -8,
    duration: SCENE,
    ease: 'none',
}, 0);

window.__timelines = { main: tl };
\`\`\`

Starting at 1.015 rather than 1 means the stage is always slightly overscanned, so the drift can never expose an edge.

## Adapt

- **Amount**: 3% of scale and about 20px of travel across a whole scene is the ceiling. If you can see it happening, it is too much — it should only be noticeable when it stops.
- **Direction**: drifting *toward* the content's centre of mass reads as settling; away from it reads as unease. Both are usable; pick deliberately.
- **Layered instead**: to give depth as well as life, drift foreground and background at different rates — see \`${SKILL_NAMES.parallaxDepthDrift}\`.
- **Under a phased graphic**: the drift runs beneath the phases and is what keeps the gaps between them alive. It does not need to react to them.
- **Rotation**: a fraction of a degree over the scene adds organic wobble. Past about 0.4° it reads as a mistake.
- **Ambient alternative**: when the composition itself should stay fixed, move the background instead — see \`${SKILL_NAMES.atmosphereGradientField}\`.

## Pitfalls

- \`ease: 'none'\` is required. Any easing makes the drift accelerate or settle, and a drift that settles is exactly the dead frame this exists to prevent.
- The stage is transform-tweened, so it must not carry a CSS \`transform\`.
- Start scaled slightly over 1. Drifting from exactly 1.0 can expose a hairline of background at the frame edge.
- Overscanned content reaches past the frame, so elements that end up outside it need \`data-l5-allow-offscreen\`.
- The tween must span the *whole* scene. A drift that ends at 5s in a 7s scene leaves the last two seconds dead, which is the failure it was meant to fix.
- Don't drift the stage *and* float individual elements *and* sweep a light across it. One source of ambient motion.
- Text inside a drifting, scaling stage is being resampled every frame; at these magnitudes that is fine, but don't push the scale far enough to soften the type.`,
};
